import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  ArrowLeft, ClipboardList, MessageSquare, Plus, BarChart3, 
  FileText, Users, Calendar, Trash2, Edit2, ExternalLink, 
  Download, Play, CheckCircle, Clock
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DataCollectionPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [project, setProject] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [projectRes, surveysRes, interviewsRes] = await Promise.all([
          axios.get(`${API}/projects/${projectId}`, { headers }),
          axios.get(`${API}/surveys/project/${projectId}`, { headers }),
          axios.get(`${API}/interviews/project/${projectId}`, { headers })
        ]);
        setProject(projectRes.data);
        setSurveys(surveysRes.data);
        setInterviews(interviewsRes.data);
      } catch (err) {
        toast.error('Failed to load project data');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [projectId, token, navigate]);

  const handleDeleteSurvey = async (surveyId) => {
    if (!window.confirm('Delete this survey and all responses?')) return;
    try {
      await axios.delete(`${API}/surveys/${surveyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSurveys(surveys.filter(s => s.id !== surveyId));
      toast.success('Survey deleted');
    } catch (err) {
      toast.error('Failed to delete survey');
    }
  };

  const handleDeleteInterview = async (interviewId) => {
    if (!window.confirm('Delete this interview schedule and all responses?')) return;
    try {
      await axios.delete(`${API}/interviews/${interviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInterviews(interviews.filter(i => i.id !== interviewId));
      toast.success('Interview schedule deleted');
    } catch (err) {
      toast.error('Failed to delete interview');
    }
  };

  const handleToggleSurveyStatus = async (surveyId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'closed' : 'active';
    try {
      await axios.put(`${API}/surveys/${surveyId}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSurveys(surveys.map(s => s.id === surveyId ? { ...s, status: newStatus } : s));
      toast.success(`Survey ${newStatus === 'active' ? 'activated' : 'closed'}`);
    } catch (err) {
      toast.error('Failed to update survey status');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const isQuantitative = project?.research_type === 'quantitative' || project?.research_type === 'mixed';
  const isQualitative = project?.research_type === 'qualitative' || project?.research_type === 'mixed';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(`/project/${projectId}`)} className="text-slate-500 hover:text-slate-700">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-slate-900">Data Collection</h1>
                <p className="text-sm text-slate-500">{project?.title}</p>
              </div>
            </div>
            <Link to={`/project/${projectId}`}>
              <Button variant="outline">
                <BarChart3 className="w-4 h-4 mr-2" />
                Go to Analysis
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Type Selection Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Quantitative - Survey */}
          <div className={`bg-white rounded-xl border-2 p-6 transition-all ${
            isQuantitative ? 'border-indigo-200 hover:border-indigo-400' : 'border-slate-200 opacity-50'
          }`}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <ClipboardList className="w-6 h-6 text-indigo-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-900 mb-1">Survey Questionnaire</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Build questionnaires with multiple question types. Share a link with respondents and collect structured data for quantitative analysis.
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">Multiple Choice</span>
                  <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">Likert Scale</span>
                  <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">Demographics</span>
                  <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">Matrix</span>
                </div>
                {isQuantitative && (
                  <Link to={`/project/${projectId}/survey/new`}>
                    <Button className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="create-survey-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Survey
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Qualitative - Interview */}
          <div className={`bg-white rounded-xl border-2 p-6 transition-all ${
            isQualitative ? 'border-emerald-200 hover:border-emerald-400' : 'border-slate-200 opacity-50'
          }`}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-900 mb-1">Interview Schedule</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Create open-ended interview questions. Record participant responses and export formatted text for qualitative analysis.
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">Open-ended</span>
                  <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">Semi-structured</span>
                  <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">In-depth</span>
                </div>
                {isQualitative && (
                  <Link to={`/project/${projectId}/interview/new`}>
                    <Button className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="create-interview-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Interview Schedule
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Existing Surveys */}
        {surveys.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-indigo-600" />
              Your Surveys ({surveys.length})
            </h2>
            <div className="space-y-4">
              {surveys.map(survey => (
                <div key={survey.id} className="bg-white rounded-lg border border-slate-200 p-4" data-testid={`survey-${survey.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-medium text-slate-900">{survey.title}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          survey.status === 'active' ? 'bg-green-100 text-green-700' :
                          survey.status === 'closed' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {survey.status}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 mb-3">{survey.description || 'No description'}</p>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          {survey.questions?.length || 0} questions
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {survey.response_count || 0}{survey.target_responses ? ` / ${survey.target_responses}` : ''} responses
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(survey.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {survey.status === 'active' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/survey/${survey.share_code}`);
                            toast.success('Survey link copied!');
                          }}
                          data-testid={`copy-link-${survey.id}`}
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Copy Link
                        </Button>
                      )}
                      <Link to={`/project/${projectId}/survey/${survey.id}/responses`}>
                        <Button variant="outline" size="sm">
                          <BarChart3 className="w-4 h-4 mr-1" />
                          Responses
                        </Button>
                      </Link>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleSurveyStatus(survey.id, survey.status)}
                      >
                        {survey.status === 'active' ? <Clock className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </Button>
                      <Link to={`/project/${projectId}/survey/${survey.id}/edit`}>
                        <Button variant="outline" size="sm">
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteSurvey(survey.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Existing Interviews */}
        {interviews.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-emerald-600" />
              Your Interview Schedules ({interviews.length})
            </h2>
            <div className="space-y-4">
              {interviews.map(interview => (
                <div key={interview.id} className="bg-white rounded-lg border border-slate-200 p-4" data-testid={`interview-${interview.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-slate-900 mb-2">{interview.title}</h3>
                      <p className="text-sm text-slate-500 mb-3">{interview.description || 'No description'}</p>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          {interview.questions?.length || 0} questions
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {interview.interviewees?.length || 0}{interview.target_interviewees ? ` / ${interview.target_interviewees}` : ''} interviews
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(interview.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Link to={`/project/${projectId}/interview/${interview.id}/record`}>
                        <Button variant="outline" size="sm" className="text-emerald-600">
                          <Plus className="w-4 h-4 mr-1" />
                          Add Response
                        </Button>
                      </Link>
                      <Link to={`/project/${projectId}/interview/${interview.id}/responses`}>
                        <Button variant="outline" size="sm">
                          <BarChart3 className="w-4 h-4 mr-1" />
                          View All
                        </Button>
                      </Link>
                      <Link to={`/project/${projectId}/interview/${interview.id}/edit`}>
                        <Button variant="outline" size="sm">
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteInterview(interview.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {surveys.length === 0 && interviews.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ClipboardList className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">No data collection instruments yet</h3>
            <p className="text-slate-500 mb-4">Create a survey or interview schedule to start collecting data</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default DataCollectionPage;
